import { TLegacyVehicle } from "../../api/types/Vehicle";
import { LegacyOrganizationAccount } from "../../api/types/Account";
import { InfoboxSettings, TAssetLocationDataPoint, TDataPoint } from "./type";

export const getVehicleUUID = (
  dataPoint:
    | Partial<TDataPoint>
    | Partial<TLegacyVehicle>
    | Partial<TAssetLocationDataPoint>
) => {
  return `${dataPoint?.accountid}__${dataPoint?.deviceid}__${dataPoint?.vehicleid?.replace(/[^A-Z0-9]/gi, "-")}`; // replace non-alphanumeric characters with hyphen
};

export const getVehicleFieldValueFromUUID = (
  uuid: string,
  field: "accountid" | "deviceid" | "vehicleid"
) => {
  return uuid.split("__")[
    ["accountid", "deviceid", "vehicleid"].indexOf(field)
  ];
};

export const mapVehicleMakeModel = (
  dataPoint: TDataPoint | TLegacyVehicle | TAssetLocationDataPoint
) => {
  return [dataPoint?.vehiclemake, dataPoint?.vehiclemodel]
    .filter((item) => !!item)
    .join(" ");
};

export const mapVehicleDisplayTitle = (
  dataPoint: TDataPoint | TLegacyVehicle | TAssetLocationDataPoint
) => {
  const vehicleTitleChunks = [
    dataPoint?.vehiclemake,
    dataPoint?.vehiclemodel,
  ].filter((item) => !!item);
  const vehicleIDs = [
    dataPoint?.deviceid,
    dataPoint?.vehicleid ??
      (dataPoint as TDataPoint | TLegacyVehicle)?.equipmenttype, // VIN
  ].filter((item) => !!item);
  return !!vehicleTitleChunks.length
    ? vehicleTitleChunks.join(" ")
    : vehicleIDs.join(" ");
};

export const mapVehicleDisplayIds = (
  dataPoint: TDataPoint | TLegacyVehicle,
  separator: string = " / "
) => {
  return [
    dataPoint?.displayname,
    dataPoint?.deviceid,
    dataPoint?.equipmenttype, // VIN
    dataPoint?.licenseplate,
    dataPoint?.devicecode,
  ]
    .filter((item) => !!item)
    .join(separator);
};

export const mapInfoBoxContentWDriver = (
  dataPoint: TDataPoint | TAssetLocationDataPoint,
  driver: string | null = null,
  isGeozone = false,
  accountSettings?: InfoboxSettings
) => {

  if (isGeozone) {
    return `
      <div class="vehicle-infobox-content ${
        isGeozone ? "Xbg-accent-blue-pale" : "bg-white" // disabled blue bg as coloring the bottom arrow is a challenge
      } Xoutline -outline-offset-2 outline-accent-blue-dark/20 rounded-lg font-semibold text-sm text-accent-blue-dark text-center break-all w-32 relative">
        <h4 class="${isGeozone ? "pt-4 pb-2" : "py-2"}">${dataPoint.deviceid}</h4>
        ${isGeozone ? '<span class="text-accent-blue-dark/50 text-[9px] absolute top-[2px] left-1/2 -translate-x-1/2">GEOZONE</span>' : ""}
      </div>
    `;
  } else {
    const id = (dataPoint?.deviceid ?? 'N/A').replace(/^\$/, ''); // I dont know where the leading $ is coming from yet
    const description = dataPoint?.description?.trim();
    const displayName = dataPoint?.displayname?.trim();

    const showAssetId = accountSettings?.infoboxassetid ?? 0;
    const showAssetDesc = accountSettings?.infoboxassetdesc ?? 0;
    const showAssetDisplay = accountSettings?.infoboxassetdisplay ?? 0;
    const showDriver = accountSettings?.infoboxdriver ?? 0;

    const sections: string[] = [];

    if (showAssetId || sections.length === 0) {
      sections.push(`<div class="font-semibold text-[15px]">ID: ${id}</div>`);
    }

    if (showAssetDesc && description) {
      sections.push(
        `<div class="text-[11px] text-gray-600 font-normal pt-1 border-t border-accent-blue-dark/10 mt-1">Desc: ${description}</div>`
      );
    }

    if (showAssetDisplay && displayName) {
      sections.push(
        `<div class="text-[11px] text-gray-600 font-normal pt-1 border-t border-accent-blue-dark/10 mt-1">Display: ${displayName}</div>`
      );
    }

    if (showDriver && driver) {
      sections.push(
        `<div class="text-[11px] text-gray-600 font-normal pt-1 border-t border-accent-blue-dark/10 mt-1">Driver: ${driver}</div>`
      );
    }

    const infoHtml = sections.join("");

    return `
      <div class="vehicle-infobox-content ${isGeozone ? "Xbg-accent-blue-pale" : "bg-white"} Xoutline -outline-offset-2 outline-accent-blue-dark/20 rounded-lg font-semibold text-sm text-accent-blue-dark text-center break-all w-32 relative">
        <h4 class="${isGeozone ? "pt-4 pb-2" : "py-2"}">${infoHtml}</h4>
        ${isGeozone ? '<span class="text-accent-blue-dark/50 text-[9px] absolute top-[2px] left-1/2 -translate-x-1/2">GEOZONE</span>' : ""}
      </div>
    `;
  }
};

export const mapInfoBoxContent = (
  dataPoint: TDataPoint | TAssetLocationDataPoint,
  isGeozone = false
) => {
  return `
    <div class="vehicle-infobox-content ${
      isGeozone ? "Xbg-accent-blue-pale" : "bg-white" // disabled blue bg as coloring the bottom arrow is a challenge
    } Xoutline -outline-offset-2 outline-accent-blue-dark/20 rounded-lg font-semibold text-sm text-accent-blue-dark text-center break-all w-32 relative">
      <h4 class="${isGeozone ? "pt-4 pb-2" : "py-2"}">${dataPoint.deviceid}</h4>
      ${isGeozone ? '<span class="text-accent-blue-dark/50 text-[9px] absolute top-[2px] left-1/2 -translate-x-1/2">GEOZONE</span>' : ""}
    </div>
  `;
  // return `
  //   <div class="vehicle-infobox-content ${
  //     isGeozone ? "bg-accent-blue-pale" : "bg-white"
  //   } outline -outline-offset-2 outline-accent-blue-dark/20 rounded-lg font-semibold text-sm text-accent-blue-dark p-2 text-center break-all w-32 relative">
  //     <h4 class="${isGeozone ? "mt-2" : ""}">${dataPoint.deviceid}</h4>
  //     ${isGeozone ? '<span class="text-accent-blue-dark/50 text-[8px] absolute top-[1px] left-1/2 -translate-x-1/2">GEOZONE</span>' : ""}
  //   </div>
  // `; // using deviceid instead of mapVehicleDisplayTitle(dataPoint)
};
